import React, { useState, useMemo } from "react";
import styles from "./styles.module.scss";

/** Comma-separated string <-> tag list input.
 *
 * Props:
 *   value: comma-separated string (current state)
 *   originalValue: comma-separated string (saved state, for undo on pre-existing tags)
 *   onChange: called with new comma-separated string
 *   placeholder: shown when no tags
 *   readOnly: if true, shows tags without input or remove buttons
 */
const TagInput = ({ value, originalValue, onChange, placeholder, readOnly }) => {
    const [input, setInput] = useState("");
    const tags = useMemo(() => (value || "").split(",").map(s => s.trim()).filter(Boolean), [value]);
    const originalTags = useMemo(() => (originalValue || "").split(",").map(s => s.trim()).filter(Boolean), [originalValue]);

    // Pre-existing tags that were removed (for strikethrough + undo)
    const removedOriginals = useMemo(() =>
        originalTags.filter(t => !tags.includes(t)),
    [originalTags, tags]);

    const addTag = (raw) => {
        const trimmed = raw.trim().toLowerCase();
        if (!trimmed) return;
        if (tags.includes(trimmed)) {
            setInput("");
            return;
        }
        onChange([...tags, trimmed].join(","));
        setInput("");
    };

    const removeTag = (tag) => {
        onChange(tags.filter(t => t !== tag).join(","));
    };

    const restoreTag = (tag) => {
        onChange([...tags, tag].join(","));
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            e.stopPropagation();
            addTag(input);
        }
        if (e.key === "Backspace" && !input && tags.length > 0) {
            removeTag(tags[tags.length - 1]);
        }
    };

    if (readOnly) {
        return (
            <div className={styles.tagInputReadOnly}>
                {tags.length > 0
                    ? tags.map(tag => (
                        <span key={tag} className={styles.tag}>{tag}</span>
                    ))
                    : <span className={styles.tagPlaceholder}>-</span>
                }
            </div>
        );
    }

    return (
        <div className={styles.tagInput} onClick={(e) => {
            e.currentTarget.querySelector("input")?.focus();
        }}>
            {tags.map(tag => {
                const isNew = originalValue !== undefined && !originalTags.includes(tag);
                return (
                    <span key={tag} className={`${styles.tag} ${isNew ? styles.tagNew : ""}`}>
                        {tag}
                        <span className={styles.tagRemove} onClick={(e) => { e.stopPropagation(); removeTag(tag); }}>&times;</span>
                    </span>
                );
            })}
            {removedOriginals.map(tag => (
                <span key={`removed-${tag}`} className={`${styles.tag} ${styles.tagRemoved}`}>
                    {tag}
                    <span className={styles.tagRestore} onClick={(e) => { e.stopPropagation(); restoreTag(tag); }}>undo</span>
                </span>
            ))}
            <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value.replace(",", ""))}
                onKeyDown={handleKeyDown}
                onBlur={() => { if (input.trim()) addTag(input); }}
                placeholder={tags.length === 0 && removedOriginals.length === 0 ? placeholder : "Add domain..."}
                className={styles.tagInputField}
            />
        </div>
    );
};

export default TagInput;
